/**
 * TruckPlanCard — Wave A.6 (User ask 2026-06-13). Quick at-a-glance
 * truck/pallet plan that lives on the Manager Dashboard, not the
 * Zone Sheet. V1 scope: shipments (day + expected + received) +
 * shared notes. No owner assignment, no priority worlds, no
 * push-first / can-wait. Inline edit mode (no modal, no page nav).
 *
 * Attention rules:
 *   • Card shows an attention state when `shipments` is empty AND
 *     notes are empty (truck plan not entered).
 *   • Each shipment row flags ⚠️ when `pallets_expected === 0`
 *     (the manager added a day but never filled the count).
 *   • Received pallets are quick-incrementable in read mode with
 *     a `+1` stepper so the receiver doesn't have to enter edit.
 */
import { useState } from "react";
import { Truck, Warning, PencilSimple, Check, Plus, Trash, X } from "@phosphor-icons/react";
import EmptyStateChip from "./EmptyStateChip";

const DAY_CHOICES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function shortClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function TruckPlanCard({
  truckPlan,
  authorName,
  onSave,
}) {
  const [editing, setEditing] = useState(false);
  const [draftShipments, setDraftShipments] = useState([]);
  const [draftNotes, setDraftNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const shipments = truckPlan?.shipments || [];
  const notes = truckPlan?.notes || "";
  const updatedAt = truckPlan?.updated_at;
  const cardEmpty = shipments.length === 0 && !notes.trim();

  // 2026-02 layout pass — when the manager hasn't entered anything AND
  // isn't currently in edit mode, collapse to a single-row rose chip
  // instead of a full card. Tapping the chip opens edit mode via the
  // same `beginEdit` handler so the surface reads as "tap here to
  // enter your truck plan" without a full ~180px empty card.

  const beginEdit = () => {
    setDraftShipments(
      shipments.length > 0
        ? shipments.map((s) => ({ ...s }))
        : [{ id: `tmp-${Date.now()}`, day: "", pallets_expected: 0, pallets_received: 0 }],
    );
    setDraftNotes(notes);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraftShipments([]);
    setDraftNotes("");
  };
  const save = async () => {
    setBusy(true);
    try {
      const cleaned = draftShipments
        .map((s) => {
          const row = {
            day: (s.day || "").trim(),
            pallets_expected: Math.max(0, parseInt(s.pallets_expected, 10) || 0),
            pallets_received: Math.max(0, parseInt(s.pallets_received, 10) || 0),
          };
          // Only forward `id` when it's a real server-stamped uuid. The
          // backend sanitizer keeps whatever id we send (`str(s.get("id"))`
          // is truthy for "tmp-…"), which used to leave the client tmp
          // id stuck on the row for one extra render after save. By
          // dropping local tmp ids here, the server always stamps a
          // fresh uuid (zone_sheets.py:1051) and read mode never shows
          // a `tmp-…` id.
          if (s.id && !String(s.id).startsWith("tmp-")) row.id = s.id;
          return row;
        })
        .filter((s) => s.day);
      await onSave({ shipments: cleaned, notes: draftNotes.trim() });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };
  const quickReceive = async (shipmentId) => {
    setBusy(true);
    try {
      const cleaned = shipments.map((s) =>
        s.id === shipmentId
          ? { ...s, pallets_received: (Number(s.pallets_received) || 0) + 1 }
          : s,
      );
      await onSave({ shipments: cleaned, notes });
    } finally {
      setBusy(false);
    }
  };
  const updateDraft = (idx, patch) => {
    setDraftShipments((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addDraftRow = () => {
    setDraftShipments((prev) => [
      ...prev,
      { id: `tmp-${Date.now()}-${prev.length}`, day: "", pallets_expected: 0, pallets_received: 0 },
    ]);
  };
  const removeDraftRow = (idx) => {
    setDraftShipments((prev) => prev.filter((_, i) => i !== idx));
  };

  if (cardEmpty && !editing) {
    return (
      <EmptyStateChip
        icon={Truck}
        tint="rose"
        label="Operations"
        message="Truck plan not entered — set this week's shipment days"
        ctaLabel="Set Plan"
        onClick={beginEdit}
        testid="truck-plan-empty-chip"
      />
    );
  }

  return (
    <section
      className={`relative rounded-3xl bg-white border shadow-md ring-1 overflow-hidden ${
        cardEmpty ? "border-rose-300/70 ring-rose-100" : "border-slate-200/70 ring-slate-100"
      }`}
      data-testid="truck-plan-card"
      data-empty={cardEmpty}
    >
      <div
        className="absolute inset-x-0 top-0 h-1 pointer-events-none"
        style={{
          background: cardEmpty
            ? "linear-gradient(90deg, #f43f5e 0%, #f97316 50%, #facc15 100%)"
            : "linear-gradient(90deg, #0066FF 0%, #06b6d4 50%, #84cc16 100%)",
        }}
      />
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100 flex-wrap gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0"
            style={{ background: cardEmpty ? "linear-gradient(135deg, #f43f5e, #f97316)" : "linear-gradient(135deg, #0066FF, #06b6d4)" }}
          >
            <Truck weight="fill" className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className={`text-[10px] uppercase tracking-[0.22em] font-black leading-none ${cardEmpty ? "text-rose-600" : "text-blue-600"}`}>
              Operations
            </p>
            <p
              className="text-lg font-black text-slate-900 leading-tight mt-1"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
            >
              Truck / Pallet Plan
            </p>
          </div>
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={cancel} disabled={busy} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-slate-100 text-[11px] font-black text-slate-700 hover:bg-slate-200 transition-colors" data-testid="truck-plan-cancel">
              <X weight="bold" className="w-3.5 h-3.5" /> Cancel
            </button>
            <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-[11px] font-black transition-colors disabled:opacity-60" style={{ background: "linear-gradient(135deg, #10b981, #14b8a6)" }} data-testid="truck-plan-save">
              <Check weight="bold" className="w-3.5 h-3.5" /> Done
            </button>
          </div>
        ) : (
          <button type="button" onClick={beginEdit} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 text-[11px] sm:text-xs font-black text-blue-700 hover:bg-blue-100 transition-colors" data-testid="truck-plan-edit">
            <PencilSimple weight="bold" className="w-3.5 h-3.5" />
            {cardEmpty ? "Set Truck Plan" : "Edit"}
          </button>
        )}
      </div>

      <div className="px-4 sm:px-5 py-4">
        {editing ? (
          <div className="space-y-3" data-testid="truck-plan-edit-mode">
            {draftShipments.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-2 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5">
                <select
                  value={s.day}
                  onChange={(e) => updateDraft(idx, { day: e.target.value })}
                  className="flex-1 min-w-0 bg-white border border-slate-200 rounded-lg px-2 py-1 text-sm font-bold text-slate-800 focus:border-blue-500 focus:outline-none"
                  data-testid={`truck-day-${idx}`}
                >
                  <option value="">Day…</option>
                  {DAY_CHOICES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <div className="flex items-center gap-1">
                  <label className="text-[10px] font-black text-blue-700 uppercase">Exp</label>
                  <input type="number" min="0" value={s.pallets_expected} onChange={(e) => updateDraft(idx, { pallets_expected: e.target.value })} className="w-14 text-center bg-white border border-slate-200 rounded-lg text-sm font-black tabular-nums text-slate-800 focus:border-blue-500 focus:outline-none" data-testid={`truck-exp-${idx}`} />
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-[10px] font-black text-emerald-700 uppercase">Rec</label>
                  <input type="number" min="0" value={s.pallets_received} onChange={(e) => updateDraft(idx, { pallets_received: e.target.value })} className="w-14 text-center bg-white border border-slate-200 rounded-lg text-sm font-black tabular-nums text-slate-800 focus:border-emerald-500 focus:outline-none" data-testid={`truck-rec-${idx}`} />
                </div>
                <button type="button" onClick={() => removeDraftRow(idx)} className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-rose-600 hover:bg-rose-100 transition-colors shrink-0" data-testid={`truck-remove-${idx}`} aria-label="Remove shipment">
                  <Trash weight="bold" className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={addDraftRow} className="w-full inline-flex items-center justify-center gap-1 py-2 rounded-2xl border-2 border-dashed border-slate-300 text-[12px] font-black text-slate-600 hover:bg-slate-50 transition-colors" data-testid="truck-add-shipment">
              <Plus weight="bold" className="w-4 h-4" /> Add shipment day
            </button>
            <textarea
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              placeholder="Truck notes (e.g. Push GLTS first. Keep Pokémon in office.)"
              rows={2}
              className="w-full bg-slate-50 ring-1 ring-slate-200 rounded-2xl px-3 py-2 text-sm font-medium text-slate-800 focus:ring-blue-300 focus:outline-none resize-none"
              maxLength={500}
              data-testid="truck-notes-input"
            />
          </div>
        ) : cardEmpty ? (
          <div className="flex items-center gap-3 rounded-2xl px-3.5 py-3 bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50 ring-1 ring-rose-100" data-testid="truck-plan-empty">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-sm shrink-0" style={{ background: "linear-gradient(135deg, #f43f5e, #f97316)" }}>
              <Warning weight="fill" className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-black text-rose-800">Truck plan not entered</p>
              <p className="text-[11px] font-bold text-rose-700/80 mt-0.5">Tap <span className="font-black">Set Truck Plan</span> to add this week&apos;s shipment days.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="truck-plan-read-mode">
            {shipments.map((s) => {
              const expMissing = !s.pallets_expected || s.pallets_expected === 0;
              return (
                <div
                  key={s.id}
                  className={`flex items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5 ring-1 ${
                    expMissing
                      ? "bg-amber-50 ring-amber-200"
                      : "bg-gradient-to-r from-blue-50 to-teal-50 ring-blue-100"
                  }`}
                  data-testid={`truck-shipment-${s.id}`}
                >
                  <div className="min-w-0 flex items-center gap-2 flex-wrap">
                    {expMissing && <Warning weight="fill" className="w-4 h-4 text-amber-600 shrink-0" />}
                    <span className="text-sm font-black text-slate-900">{s.day}</span>
                    {expMissing ? (
                      <span className="text-[11px] font-bold text-amber-700">
                        expected pallets missing
                        {Number(s.pallets_received) > 0 && (
                          <>
                            {" · "}
                            <span className="tabular-nums text-emerald-700">{s.pallets_received}</span> received
                          </>
                        )}
                      </span>
                    ) : (
                      <span className="text-[11px] font-bold text-slate-600">
                        <span className="tabular-nums">{s.pallets_expected}</span> expected
                        {" · "}
                        <span className="tabular-nums text-emerald-700">{s.pallets_received}</span> received
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => quickReceive(s.id)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white ring-1 ring-emerald-200 text-[11px] font-black text-emerald-700 hover:bg-emerald-50 transition-colors shrink-0 disabled:opacity-50"
                    data-testid={`truck-receive-plus1-${s.id}`}
                  >
                    +1 received
                  </button>
                </div>
              );
            })}
            {notes && (
              <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-3.5 py-2.5" data-testid="truck-plan-notes">
                {notes}
              </p>
            )}
            {(authorName || updatedAt) && (
              <p className="text-[11px] font-bold text-slate-500 mt-2">
                Updated
                {authorName ? <> by <span className="text-slate-700">{authorName}</span></> : null}
                {updatedAt ? <> · {shortClock(updatedAt)}</> : null}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
